import { Privacy } from '@/models/enums';
import { AppContextProvider, PrivacyPolicyStatus } from '@kf/ic2fx';
import { Button, Grid } from '@mui/material';
import { AxiosResponse } from 'axios';
import { AuthenticationStatus, ErrorMessage } from 'models/enum';
import React, { ReactElement, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import UnsupportedDevicePage from 'shared/components/unsupportedDevicePage';
import { getAuthErrorMessage } from 'utilities/authorizationUtils';
import { eeEnvVariables, storagePersistedKey } from 'utilities/constants';
import usePrivacyPolicyStatus from '../hooks/usePrivacyPolicyStatus';
import { IEmployeeAuth } from '../models/IAuth.interface';
import PrivacyPolicyWall from './PrivacyPolicyWall';
import  {TeamsAppAuthProvider}  from './TeamsAppAuthProvider';
import { _IS_MICRO_FRONTEND__, WebAppAuthProvider } from './WebAppAuthProvider';

const shouldAvoidAuth = _IS_MICRO_FRONTEND__ ? false : true;
interface IAuthorizationProviderOwnProps {
  children: ReactElement | ReactElement[];
  appIsOnTeams: boolean;
  isWindowSizeUnsupported: boolean;
}

export interface IAuthorizationProviderDispatchProps {
  saveSSOToken: (token: string, roles: string[]) => void;
}

export type IAuthorizationProviderParentProps = IAuthorizationProviderOwnProps &
  IAuthorizationProviderDispatchProps;

const AuthProvider = ({
  children,
  saveSSOToken,
  appIsOnTeams,
  isWindowSizeUnsupported,
}: IAuthorizationProviderParentProps) => {
  // const { t: translate } = useTranslation();
  const [authStatus, setAuthStatus] = useState<IEmployeeAuth>({
    status: AuthenticationStatus.Initial,
  });
  const [errorMessage, setErrorMessage] = useState<string>(ErrorMessage.defaultError);

  const {
    getPrivacyPolicyStatus,
    onFailure,
    status: privacyPolicyStatus,
    loading: loadingPrivacyPolicy,
    error: privacyPolicyError,
  } = usePrivacyPolicyStatus();

  const handlePrivacyPolicyStatus = (response: AxiosResponse) => {
    if (response?.status === 200 && authStatus.token !== undefined) {
      getPrivacyPolicyStatus(authStatus.token);
    } else {
      setAuthStatus({ status: AuthenticationStatus.Failed });
      setErrorMessage(ErrorMessage.commonError);
    }
  };

  useEffect(() => {
    if (authStatus.token) {
      getPrivacyPolicyStatus(authStatus.token);
    }
  }, [authStatus, shouldAvoidAuth]);

  useEffect(() => {
    if (privacyPolicyError) {
      setErrorMessage(ErrorMessage.commonError);
    }
  }, [privacyPolicyError]);

  useEffect(() => {
    if (
      authStatus.status === AuthenticationStatus.Succeeded &&
      authStatus.token &&
      (shouldAvoidAuth || privacyPolicyStatus === Privacy.PrivacyPolicyStatus.ACKNOWLEDGED || true) // Temporary bypass for preview mode
    ) {
      saveSSOToken(authStatus.token, []);
      localStorage.removeItem(storagePersistedKey.MSALPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authStatus, privacyPolicyStatus]);

  const tryAgainHandler = () => {
    localStorage.clear();
    sessionStorage.clear();
    if (appIsOnTeams) window.location.reload();
    else window.location.assign(window.location.origin);
  };

 

  // const FallbackScreen = (): ReactElement => {
  //   return (
  //     <Grid
  //       id='invalid-user-agent'
  //       data-testid='invalid-user-agent'
  //       sx={{ px: 2 }}
  //       container
  //       direction='column'
  //       alignContent='center'
  //       alignItems='center'
  //       rowSpacing={2}
  //     >
  //       {errorMessage === ErrorMessage.invalidUserAgent ? (
  //         <UnsupportedDevicePage />
  //       ) : (
  //         <>
  //           <Grid item sx={{ mt: 4 }}>
  //             {translate(getAuthErrorMessage(errorMessage))}
  //           </Grid>
  //           <Grid item>
  //             <Button variant='contained' onClick={() => tryAgainHandler()}>
  //               {translate('goBack')}
  //             </Button>
  //           </Grid>
  //         </>
  //       )}
  //     </Grid>
  //   );
  // };

  return (
    <div data-testid='authProvider-root'>
      {appIsOnTeams ? (
        <TeamsAppAuthProvider  
          setAuthStatus={setAuthStatus}
          authStatus={authStatus}
          setErrorMessage={setErrorMessage}
          isWindowSizeUnsupported={isWindowSizeUnsupported} />
      ) : (
        <WebAppAuthProvider
          setAuthStatus={setAuthStatus}
          authStatus={authStatus}
          setErrorMessage={setErrorMessage}
          isWindowSizeUnsupported={isWindowSizeUnsupported}
        />
      )}
      {authStatus.status === AuthenticationStatus.Failed ? <h1>Failed</h1> : children}
      {!shouldAvoidAuth && (
        <PrivacyPolicyWall
          privacyPolicyStatus={privacyPolicyStatus}
          fetchingStatus={loadingPrivacyPolicy}
          onAccept={handlePrivacyPolicyStatus}
          onFailure={onFailure}
          privacyPolicyError={privacyPolicyError}
        />
      )}
    </div>
  );
};

export default AuthProvider;
